import { useState, useEffect, useMemo } from "react";
import { db } from "firebaseConfig";
import useFirestore from "hooks/useFirestore";
import { GenericConverter } from "utils/firestoreConverters";
import { mergeCalendarEvents, useMyEventsByListing } from "hooks/useMyEventsByListing";
import type { CalendarEvent } from "../@types/sharedSchema";

/**
 * Every task/meeting on one listing that the current user may see: their OWN
 * events plus their teammates' org-shared ones.
 *
 * The two halves are separate listeners because Firestore cannot OR across
 * different fields (`userId == me` vs `organizationIds array-contains myOrg`)
 * — the same shape `useListingNotes` already uses for notes.
 *
 * The personal half is not an optimisation, it is the fix for the reported bug
 * ("a task created from a property file goes into the calendar but never shows
 * up in that property file"): see `useMyEventsByListing` for why the org half
 * alone misses 46% of listing-linked events.
 */
export function useListingEvents(listingId: string): {
  events: CalendarEvent[];
  isLoading: boolean;
} {
  const [orgEvents, setOrgEvents] = useState<CalendarEvent[]>([]);
  const [loadingOrgEvents, setLoadingOrgEvents] = useState(false);

  const { organizationId } = useFirestore();
  const { eventsByListing: myEventsByListing, isLoading: loadingMyEvents } = useMyEventsByListing();

  useEffect(() => {
    // A realtor with no agency has no teammates' events to load — their own
    // half above is the whole answer, so this is a no-op, never an empty tab.
    if (!organizationId) {
      setOrgEvents([]);
      setLoadingOrgEvents(false);
      return;
    }

    setLoadingOrgEvents(true);

    // Query by organizationIds and narrow to this listing client-side (same
    // pattern as useOrgEventsByListing). The {path=**}/calendar-events rule
    // only allows reads the query can PROVE (creator or org overlap), so a
    // query constrained only by listingIds is denied outright — and Firestore
    // permits a single array-contains per query, so listingIds cannot ride
    // the query alongside organizationIds. Identical queries across mounts
    // share one underlying watch stream, so this dedupes too.
    const unsubscribe = db
      .collectionGroup("calendar-events")
      .where("organizationIds", "array-contains", organizationId)
      .withConverter(GenericConverter<CalendarEvent>())
      .onSnapshot(
        (snapshot) => {
          const eventsData = snapshot.docs
            .map((doc) => doc.data())
            .filter((event) => (event.listingIds ?? []).includes(listingId));

          setOrgEvents(eventsData);
          setLoadingOrgEvents(false);
        },
        (error) => {
          console.error("Error fetching organization events: ", error);
          setLoadingOrgEvents(false);
        }
      );

    return unsubscribe;
  }, [listingId, organizationId]);

  // Mine first: an own event that is also org-stamped comes back twice.
  const events = useMemo(
    () => mergeCalendarEvents(myEventsByListing.get(listingId) ?? [], orgEvents),
    [myEventsByListing, listingId, orgEvents]
  );

  return { events, isLoading: loadingMyEvents || loadingOrgEvents };
}
